import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const A4_MM = { width: 210, height: 297 };
const A4_RATIO = A4_MM.width / A4_MM.height; // ≈ 0.7071

async function waitForImages(node) {
  const imgs = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth) return resolve();
          img.addEventListener("load", () => resolve(), { once: true });
          img.addEventListener("error", () => resolve(), { once: true });
        }),
    ),
  );
  // Give the browser one paint frame to settle backgrounds/transforms.
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}

async function nodeToCanvas(node) {
  await waitForImages(node);
  const rect = node.getBoundingClientRect();
  return await html2canvas(node, {
    scale: 3, // higher resolution capture
    width: rect.width,
    height: rect.height,
    windowWidth: document.documentElement.clientWidth,
    backgroundColor: "#ffffff",
    useCORS: true,
    allowTaint: true,
    logging: false,
    imageTimeout: 0,
  });
}

// Place the captured canvas onto an A4 page. If the canvas aspect ratio
// does not exactly match A4, letterbox it so the picture is NEVER squished.
function placeOnA4(pdf, canvas) {
  const imgData = canvas.toDataURL("image/jpeg", 0.95);
  const canvasRatio = canvas.width / canvas.height;
  let drawW = A4_MM.width;
  let drawH = A4_MM.height;
  if (canvasRatio > A4_RATIO) {
    // Canvas is wider than A4 → fit width, letterbox top/bottom
    drawH = A4_MM.width / canvasRatio;
  } else if (canvasRatio < A4_RATIO) {
    // Canvas is taller than A4 → fit height, letterbox left/right
    drawW = A4_MM.height * canvasRatio;
  }
  const offsetX = (A4_MM.width - drawW) / 2;
  const offsetY = (A4_MM.height - drawH) / 2;
  pdf.addImage(imgData, "JPEG", offsetX, offsetY, drawW, drawH, undefined, "FAST");
}

export async function downloadPageAsPdf(node, filename) {
  const canvas = await nodeToCanvas(node);
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  placeOnA4(pdf, canvas);
  pdf.save(filename);
}

export async function previewPageAsPdf(node) {
  const canvas = await nodeToCanvas(node);
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  placeOnA4(pdf, canvas);
  openPdfInNewTab(pdf);
}

export async function downloadAllAsPdf(nodes, filename) {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (!node) continue;
    const canvas = await nodeToCanvas(node);
    if (i > 0) pdf.addPage("a4", "portrait");
    placeOnA4(pdf, canvas);
  }
  pdf.save(filename);
}

export async function previewAllAsPdf(nodes) {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (!node) continue;
    const canvas = await nodeToCanvas(node);
    if (i > 0) pdf.addPage("a4", "portrait");
    placeOnA4(pdf, canvas);
  }
  openPdfInNewTab(pdf);
}

// Convenience wrapper used by the UI.
export const openPdfPreview = {
  page: previewPageAsPdf,
  all: previewAllAsPdf,
};

function openPdfInNewTab(pdf) {
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  // NOTE: passing "noopener" in the features string makes window.open
  // return null by spec, which would confuse a null-check for
  // popup-blocking. Open without features and null the opener manually.
  let w = null;
  try {
    w = window.open(url, "_blank");
    if (w) w.opener = null;
  } catch (_e) {
    w = null;
  }
  if (!w) {
    // Popup blocked — fall back to an anchor click.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// Also export a PNG (used by passport but harmless here).
export async function downloadPageAsPng(node, filename) {
  const canvas = await nodeToCanvas(node);
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }, "image/png");
}
